import {
  createSlice,
  // Draft,
} from '@reduxjs/toolkit';
import {
  getListModifierDefaults,
  listModifierDefaultFunctions,
  ListModifierDefaultsParams,
} from 'common/features/FilteredProfilesList/config/defaults';
import { ArtistOptionType } from 'common/features/FilteredProfilesList/config/artist';
import {
  AppliedListingModifier,
  BaseOptionValueType,
  filterTypes,
  isOptionType,
  FILTERS_DEFAULT_VALUE,
  FilteredProfileQueryHandlers,
  PROFILE_QUERY_CONDITION,
  PROFILE_QUERY_CONDITION_IN,
  PROFILE_QUERY_SORT,
} from 'common/features/FilteredProfilesList/config/types';
import { selectOptionFromAppliedModifier } from 'common/features/FilteredProfilesList/hooks/useFilterUtils';
import { ExploreArtistCommonFilterFieldsFragment } from 'common/schema/commonSchemaRemoteOperationTypes';

type ListStateType = 'READY' | 'FETCH' | 'INITIALIZE' | 'LOADING';

export enum ProfileListType {
  EXPLORE = 'EXPLORE',
  COLLECTION = 'COLLECTION',
}

type ListModifierType<T extends ExploreArtistCommonFilterFieldsFragment> = {
  identifier: string;
  profiles: T[];
  listState: ListStateType;
  filters: { [filterKey in filterTypes]: AppliedListingModifier };
  queryChanged: number;
  filterChanged: number;
  hasModifiersApplied: boolean;
};

type listModifiersSliceType = {
  [listTypeKey in ProfileListType]: ListModifierType<FilteredProfileQueryHandlers[listTypeKey]['queryProfileType']>;
} & {
  showCasing: ProfileListType | null;
};

const listTypeDefaultState: <T extends ProfileListType>(
  params: ListModifierDefaultsParams
) => ListModifierType<FilteredProfileQueryHandlers[T]['queryProfileType']> = (params) => ({
  identifier: '',
  profiles: [],
  listState: 'READY',
  filters: getListModifierDefaults(params),
  hasModifiersApplied: false,
  filterChanged: 0,
  queryChanged: 0,
});

const getInitialState: () => listModifiersSliceType = () => ({
  [ProfileListType.EXPLORE]: listTypeDefaultState<ProfileListType.EXPLORE>({
    variant: ProfileListType.EXPLORE,
  }),
  [ProfileListType.COLLECTION]: listTypeDefaultState<ProfileListType.COLLECTION>({
    variant: ProfileListType.COLLECTION,
  }),
  showCasing: ProfileListType.EXPLORE,
});

type SetProfileListType = { type: ProfileListType; profiles: listModifiersSliceType[ProfileListType]['profiles'] };

const profileListModifiersSlice = createSlice({
  name: 'profileListModifiers',
  initialState: getInitialState(),
  reducers: {
    INITIALIZE_TYPE: (
      state,
      action: {
        payload: {
          type: ProfileListType;
          identifier: string;
        };
      }
    ) => {
      const { type, identifier } = action.payload;
      if (!identifier || identifier === state[type].identifier) {
        return state;
      }

      return {
        ...state,
        [type]: {
          ...listTypeDefaultState({
            variant: type,
          }),
          identifier,
          listState: 'INITIALIZE',
        },
        showCasing: type,
      };
    },
    ADD_CUSTOM_OPTIONS: (
      state,
      action: {
        payload: {
          type: ProfileListType;
          artistOptions?: ArtistOptionType[];
          sortCollectionId?: number;
        };
      }
    ) => {
      const { type, sortCollectionId, artistOptions } = action.payload;
      const workingState = state[type];

      if (artistOptions) {
        workingState.filters = {
          ...workingState.filters,
          ARTIST: listModifierDefaultFunctions.ARTIST(artistOptions),
        };
      }
      if (sortCollectionId) {
        workingState.filters = {
          ...workingState.filters,
          SORT: listModifierDefaultFunctions.SORT(sortCollectionId, type),
        };
      }
      return state;
    },
    UPDATE_SELECTED_OPTIONS: (
      state,
      action: {
        payload: {
          type: ProfileListType;
          options: {
            key: filterTypes;
            value: BaseOptionValueType;
          }[];
        };
      }
    ) => {
      const { type, options } = action.payload;
      if (state[type].listState !== 'READY') {
        return state;
      }
      const workingState = state[type];
      let queryChanged = false;
      let filterChanged = false;
      options.forEach((selectedOption) => {
        const { key, value } = selectedOption;
        const affectedModifier = workingState.filters[key];
        const newSelectedOptions = selectOptionFromAppliedModifier(affectedModifier, value);

        const affectedOption = affectedModifier.allOptions.find((option) => option.value === value);
        if (
          affectedOption &&
          (isOptionType<PROFILE_QUERY_CONDITION>(affectedOption, 'PROFILE_QUERY_CONDITION') ||
            isOptionType<PROFILE_QUERY_CONDITION_IN>(affectedOption, 'PROFILE_QUERY_CONDITION_IN') ||
            isOptionType<PROFILE_QUERY_SORT>(affectedOption, 'PROFILE_QUERY_SORT'))
        ) {
          queryChanged = true;
        }
        filterChanged = true;

        const newAppliedListingModifierState: AppliedListingModifier = {
          ...affectedModifier,
          selectedOptions: newSelectedOptions,
        };

        workingState.filters = {
          ...workingState.filters,
          [key]: newAppliedListingModifierState,
        };
      });

      workingState.hasModifiersApplied = !!Object.values(workingState.filters).find(
        (modifier) => !modifier.selectedOptions.includes(FILTERS_DEFAULT_VALUE)
      );

      if (queryChanged) {
        workingState.queryChanged += 1;
      }
      if (queryChanged || filterChanged) {
        workingState.filterChanged += 1;
      }
      state[type] = { ...workingState } as any;
      // state[type] = ({ ...workingState } as Draft<ListModifierType<FilteredProfileQueryHandlers[ProfileListType]['queryProfileType']>>);
      return state;
      // { ...state, [type]: { ...workingState } };
    },
    REFRESH: (
      state,
      {
        payload: { type },
      }: {
        payload: {
          type: ProfileListType;
        };
      }
    ) => {
      state[type].queryChanged += 1;
      return state;
    },
    UPDATE_LIST_STATE: (
      state,
      action: {
        payload: {
          type: ProfileListType;
          newState: ListStateType;
        };
      }
    ) => ({
      ...state,
      [action.payload.type]: {
        ...state[action.payload.type],
        listState: action.payload.newState,
      },
    }),
    SET_PROFILES: (
      state,
      action: {
        payload: SetProfileListType;
      }
    ) => ({
      ...state,
      [action.payload.type]: {
        ...state[action.payload.type],
        profiles: action.payload.profiles ?? [],
      },
    }),
  },
});

export const profileListActions = profileListModifiersSlice.actions;

export default profileListModifiersSlice;
